import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  CaretLeft,
  CaretRight,
  CaretDown,
  CheckCircle,
  PlayCircle,
  GraduationCap,
  List,
  X,
  SidebarSimple,
  Lock,
} from "@phosphor-icons/react";

export default function LessonView() {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Strict compliance mode: block forward seek + force 1x speed for everyone
  // EXCEPT admins (who need to scrub through to review). We still allow rewind
  // to any earlier point so learners can re-listen as much as they want.
  const strictWatch = user?.role !== "admin";
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState({});
  const [error, setError] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Persist learner's "hide curriculum" preference across navigation/refresh
  const [sidebarHidden, setSidebarHidden] = useState(() => {
    try {
      return localStorage.getItem("learnhub.lessonSidebarHidden") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(
        "learnhub.lessonSidebarHidden",
        sidebarHidden ? "1" : "0"
      );
    } catch {
      /* ignore */
    }
  }, [sidebarHidden]);
  const videoRef = useRef(null);
  const lastSentRef = useRef(0);
  // High-water mark of the actual playback position the user has reached.
  // Drives the strict-watch seek guard; persists across pause/resume within a lesson.
  const maxWatchedRef = useRef(0);
  // Mirror of maxWatchedRef into state so we can re-render Next/Done buttons
  // when the threshold is crossed (refs don't trigger re-renders).
  const [maxWatchedSec, setMaxWatchedSec] = useState(0);
  // De-bounce noisy seek-block toasts (one per ~2s)
  const lastSeekWarnRef = useRef(0);

  // Which modules are collapsed in the curriculum sidebar (id -> bool).
  // Default = all expanded; clicking the chevron toggles. The module containing
  // the current lesson auto-expands when the user navigates so they can see context.
  const [collapsedModules, setCollapsedModules] = useState({});

  const flatLessons = useMemo(() => {
    if (!course) return [];
    const out = [];
    for (const m of course.modules || []) {
      for (const l of m.lessons || []) {
        out.push({ ...l, moduleTitle: m.title, moduleId: m.id });
      }
    }
    return out;
  }, [course]);

  const currentIdx = useMemo(
    () => flatLessons.findIndex((l) => l.id === lessonId),
    [flatLessons, lessonId]
  );
  const currentLesson = currentIdx >= 0 ? flatLessons[currentIdx] : null;
  const prevLesson = currentIdx > 0 ? flatLessons[currentIdx - 1] : null;
  const nextLesson = currentIdx >= 0 && currentIdx < flatLessons.length - 1 ? flatLessons[currentIdx + 1] : null;

  // Auto-expand the module containing the current lesson whenever the user navigates
  useEffect(() => {
    if (currentLesson?.moduleId) {
      setCollapsedModules((c) => ({ ...c, [currentLesson.moduleId]: false }));
    }
  }, [currentLesson?.moduleId]);

  useEffect(() => {
    (async () => {
      try {
        const [courseRes, progRes] = await Promise.all([
          api.get(`/courses/${courseId}`),
          api.get(`/progress/${courseId}`).catch(() => ({ data: {} })),
        ]);
        setCourse(courseRes.data);
        setProgress(progRes.data || {});
        // Auto-enrol (if not already)
        api.post(`/enroll/${courseId}`).catch(() => {});
      } catch (e) {
        setError(formatApiError(e, "Could not load lesson."));
      }
    })();
  }, [courseId]);

  // Send progress while watching, throttled to every ~10 seconds.
  //
  // IMPORTANT: we send the HIGH-WATER MARK (the furthest position the user has
  // ever reached on this lesson), not the playhead's current position. Otherwise
  // rewinding to re-listen would *overwrite* the user's recorded progress with a
  // smaller value, regressing the resume-point and breaking the 95%-completion
  // check. We also defensively keep any previously-saved progress to guard
  // against early-fire edge cases (pause before onLoadedMetadata seeded the ref).
  const sendProgress = async (completed = false) => {
    if (!videoRef.current || !currentLesson) return;
    const playhead = videoRef.current.currentTime || 0;
    const prevSaved = Number(progress[lessonId]?.watched_seconds) || 0;
    const watched = Math.max(playhead, maxWatchedRef.current, prevSaved);
    const duration = videoRef.current.duration || currentLesson.duration_seconds || 0;
    try {
      const { data } = await api.post("/progress", {
        course_id: courseId,
        lesson_id: lessonId,
        watched_seconds: watched,
        duration_seconds: duration,
        completed,
      });
      setProgress((p) => ({
        ...p,
        [lessonId]: {
          ...(p[lessonId] || {}),
          watched_seconds: watched,
          duration_seconds: duration,
          completed: data.completed,
        },
      }));
    } catch (_e) {
      /* swallow - progress is best-effort */
    }
  };

  const onTimeUpdate = () => {
    if (!videoRef.current) return;
    const t = videoRef.current.currentTime;
    // Advance the high-water mark with normal playback (paused or not, we just
    // record how far the user has legitimately reached).
    if (t > maxWatchedRef.current) {
      maxWatchedRef.current = t;
      // Re-render the Next/Done buttons when the user crosses the threshold.
      // Snap to whole seconds so we don't trigger 30+ renders per second.
      setMaxWatchedSec((prev) => (Math.floor(t) > prev ? Math.floor(t) : prev));
    }
    if (t - lastSentRef.current >= 10) {
      lastSentRef.current = t;
      sendProgress(false);
    }
  };

  // Strict-watch enforcement: block any forward seek past the high-water mark.
  // Rewind is always allowed (user can re-listen to any earlier portion).
  // Admins are exempt — they may scrub freely.
  const onSeeking = () => {
    if (!videoRef.current || !strictWatch) return;
    const t = videoRef.current.currentTime;
    // Allow a 1s grace because the browser's "ended → loop back to 0" event
    // also triggers a seek, and small float jitter shouldn't trip the guard.
    if (t > maxWatchedRef.current + 1) {
      videoRef.current.currentTime = maxWatchedRef.current;
      const now = Date.now();
      if (now - lastSeekWarnRef.current > 2000) {
        lastSeekWarnRef.current = now;
        toast.info("Skipping ahead is disabled", {
          description: "Watch the full video to mark this lesson complete.",
        });
      }
    }
  };

  // Strict-watch enforcement: force 1× playback. Admins exempt.
  const onRateChange = () => {
    if (!videoRef.current || !strictWatch) return;
    if (Math.abs(videoRef.current.playbackRate - 1) > 0.01) {
      videoRef.current.playbackRate = 1;
      const now = Date.now();
      if (now - lastSeekWarnRef.current > 2000) {
        lastSeekWarnRef.current = now;
        toast.info("Playback speed locked at 1×", {
          description: "Strict watch mode requires full-length playback.",
        });
      }
    }
  };

  const onEnded = () => {
    sendProgress(true);
  };

  // Mark current lesson complete then navigate. Used by Next/Done buttons so
  // that explicitly moving on is treated as "I'm done with this one".
  const markCompleteAndGo = async (targetPath) => {
    try {
      await sendProgress(true);
    } catch {
      /* progress is best-effort, never block navigation */
    }
    navigate(targetPath);
  };

  // Resume from the last watched position when the lesson loads
  const onLoadedMetadata = () => {
    if (!videoRef.current) return;
    // Always start with no high-water mark for the new video
    maxWatchedRef.current = 0;
    setMaxWatchedSec(0);
    // Force 1× playback rate at load (some browsers persist the last rate)
    if (strictWatch) videoRef.current.playbackRate = 1;
    const p = progress[lessonId];
    if (!p) return;
    const watched = Number(p.watched_seconds) || 0;
    const duration = videoRef.current.duration || Number(p.duration_seconds) || 0;
    // Don't auto-resume if already completed or near the end (let learner start over)
    if (p.completed || (duration && watched >= duration * 0.95)) {
      // But if it was already completed, mark them as having watched enough so the
      // Next/Done buttons stay enabled (they earned it previously).
      if (p.completed) {
        maxWatchedRef.current = duration || watched;
        setMaxWatchedSec(Math.floor(duration || watched));
      }
      return;
    }
    if (watched > 5) {
      try {
        videoRef.current.currentTime = watched;
        // Seed the high-water mark to the resumed position so they can rewind to 0
        // but can't skip past where they previously reached.
        maxWatchedRef.current = watched;
        setMaxWatchedSec(Math.floor(watched));
        lastSentRef.current = watched;
      } catch (_e) {
        /* some browsers reject seek before metadata fully loaded - ignored */
      }
    }
  };

  // Reset throttle + high-water when lesson changes
  useEffect(() => {
    lastSentRef.current = 0;
    maxWatchedRef.current = 0;
    setMaxWatchedSec(0);
  }, [lessonId]);

  // Whether the user has watched enough to unlock Next/Done buttons.
  // Admins are always unlocked. Otherwise the high-water mark must be ≥ 95% of duration.
  // Also unlocked if the lesson is already marked complete from a previous session.
  const currentProgress = progress[lessonId];
  const lessonDuration =
    videoRef.current?.duration || Number(currentProgress?.duration_seconds) || 0;
  const watchedRatio = lessonDuration > 0 ? maxWatchedSec / lessonDuration : 0;
  const hasVideo = !!currentLesson?.video_path;
  const canFinish =
    !strictWatch ||
    !hasVideo ||
    currentProgress?.completed ||
    (lessonDuration > 0 && watchedRatio >= 0.95);
  const remainingPct = canFinish ? 0 : Math.max(0, Math.ceil((1 - watchedRatio) * 100));

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-sm text-[#993333]">{error}</div>
    );
  }
  if (!course || !currentLesson) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-sm text-[#8A8A85]">
        Loading lesson...
      </div>
    );
  }

  const videoSrc = currentLesson.video_path ? fileUrl(currentLesson.video_path) : "";

  const sidebar = (
    <div className="bg-[#161614] border-l border-[#2A2A26] h-full overflow-y-auto text-[#EAEAE6]">
      <div className="p-5 border-b border-[#2A2A26]">
        <Link to={`/courses/${courseId}`} className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#E25822] flex items-center gap-2 hover:text-[#F08456] transition-colors">
          <CaretLeft size={14} /> Back to course
        </Link>
        <h2 className="font-heading text-lg font-semibold tracking-tight mt-3 text-white">{course.title}</h2>
      </div>
      <div className="p-2">
        {(course.modules || []).map((m, mIdx) => {
          const collapsed = !!collapsedModules[m.id];
          const lessonCount = (m.lessons || []).length;
          const completedInModule = (m.lessons || []).filter((l) => progress[l.id]?.completed).length;
          return (
            <div key={m.id} className="mb-3">
              <button
                type="button"
                onClick={() =>
                  setCollapsedModules((c) => ({ ...c, [m.id]: !c[m.id] }))
                }
                data-testid={`sidebar-module-toggle-${m.id}`}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left rounded-md hover:bg-white/[0.04] transition-colors"
              >
                <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#8A8A85] truncate">
                  {String(mIdx + 1).padStart(2, "0")} · {m.title}
                </span>
                <span className="flex items-center gap-2 text-[10px] text-[#5C5C58] shrink-0">
                  <span>{completedInModule}/{lessonCount}</span>
                  {collapsed ? <CaretRight size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                </span>
              </button>
              {!collapsed &&
                (m.lessons || []).map((l) => {
                  const isCurrent = l.id === lessonId;
                  const done = progress[l.id]?.completed;
                  return (
                    <Link
                      key={l.id}
                      to={`/courses/${courseId}/lesson/${l.id}`}
                      data-testid={`sidebar-lesson-${l.id}`}
                      onClick={() => setDrawerOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        isCurrent
                          ? "bg-[#E25822] text-white font-semibold"
                          : "text-[#B5B5AE] hover:bg-white/[0.06] hover:text-white"
                      }`}
                    >
                      {done ? (
                        <CheckCircle size={18} weight="fill" color="#4A6B53" />
                      ) : (
                        <PlayCircle size={18} weight={isCurrent ? "fill" : "duotone"} color="#E25822" />
                      )}
                      <span className="line-clamp-1 flex-1">{l.title}</span>
                    </Link>
                  );
                })}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div
      className={`min-h-screen bg-[#0F0F0D] grid ${
        sidebarHidden ? "lg:grid-cols-1" : "lg:grid-cols-[1fr_360px]"
      }`}
    >
      {/* Main */}
      <div className="flex flex-col min-w-0">
        {/* Header */}
        <header className="border-b border-[#2A2A26] sticky top-0 bg-[#161614] z-30">
          <div className="px-4 lg:px-8 h-16 flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="lesson-home-link">
              <div className="w-8 h-8 rounded-lg bg-[#E25822] flex items-center justify-center">
                <GraduationCap size={17} weight="bold" color="#FFFFFF" />
              </div>
              <span className="font-heading font-semibold tracking-tight text-white">UzviS Learning</span>
            </Link>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#8A8A85] truncate">{currentLesson.moduleTitle}</div>
              <h1 className="font-heading text-base font-semibold tracking-tight truncate text-white" data-testid="lesson-title">
                {currentLesson.title}
              </h1>
            </div>
            <button
              onClick={() => setSidebarHidden(!sidebarHidden)}
              className="hidden lg:inline-flex p-2 -mr-1 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              data-testid="lesson-toggle-curriculum"
              title={sidebarHidden ? "Show curriculum" : "Hide curriculum"}
              aria-label={sidebarHidden ? "Show curriculum" : "Hide curriculum"}
            >
              <SidebarSimple size={20} weight={sidebarHidden ? "regular" : "fill"} />
            </button>
            <button
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden p-2 -mr-2 text-white"
              data-testid="lesson-toggle-sidebar"
            >
              <List size={22} />
            </button>
          </div>
        </header>

        {/* Player */}
        <div className="bg-black">
          {videoSrc ? (
            <video
              ref={videoRef}
              key={lessonId}
              src={videoSrc}
              controls
              controlsList={strictWatch ? "nodownload noplaybackrate" : "nodownload"}
              disablePictureInPicture={strictWatch}
              onLoadedMetadata={onLoadedMetadata}
              onTimeUpdate={onTimeUpdate}
              onSeeking={onSeeking}
              onRateChange={onRateChange}
              onEnded={onEnded}
              onPause={() => sendProgress(false)}
              className="w-full aspect-video bg-black"
              data-testid="lesson-video-player"
            >
              Your browser does not support video playback.
            </video>
          ) : (
            <div className="w-full aspect-video flex items-center justify-center text-white text-sm" data-testid="lesson-no-video">
              <div className="text-center">
                <PlayCircle size={56} className="mx-auto mb-3 opacity-50" />
                <div className="eyebrow text-white/70">No video uploaded for this lesson yet.</div>
              </div>
            </div>
          )}
        </div>

        {/* Description + nav */}
        <div className="page-fade px-4 lg:px-8 py-8 max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#8A8A85] mb-2">{currentLesson.moduleTitle}</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-4 text-white">{currentLesson.title}</h2>
          {strictWatch && (
            <div
              data-testid="strict-watch-badge"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#E25822]/10 border border-[#E25822]/30 text-[#E25822] text-xs font-semibold mb-5"
              title="Watch the full video to mark this lesson complete. Skipping ahead and changing playback speed are disabled."
            >
              <Lock size={12} weight="bold" />
              Mandatory watch · no skipping · 1× speed
            </div>
          )}
          {currentLesson.description ? (
            <p className="text-[#B5B5AE] leading-relaxed mb-8 whitespace-pre-line">{currentLesson.description}</p>
          ) : (
            <p className="text-[#5C5C58] italic mb-8">No description provided.</p>
          )}

          <div className="flex flex-col sm:flex-row gap-3 border-t border-[#2A2A26] pt-6">
            {prevLesson ? (
              <Link
                to={`/courses/${courseId}/lesson/${prevLesson.id}`}
                className="flex-1 rounded-lg border border-[#2A2A26] hover:border-[#3D3D38] hover:bg-white/[0.04] px-5 py-3 inline-flex items-center gap-3 text-[#EAEAE6] transition-colors"
                data-testid="lesson-prev-button"
              >
                <CaretLeft size={18} />
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#8A8A85]">Previous</div>
                  <div className="text-sm font-semibold truncate">{prevLesson.title}</div>
                </div>
              </Link>
            ) : (
              <div className="flex-1" />
            )}
            {nextLesson ? (
              <button
                onClick={() => canFinish && markCompleteAndGo(`/courses/${courseId}/lesson/${nextLesson.id}`)}
                disabled={!canFinish}
                className={`flex-1 rounded-lg px-5 py-3 inline-flex items-center justify-end gap-3 transition-colors text-left ${
                  canFinish
                    ? "bg-[#E25822] hover:bg-[#C44E1A] text-white"
                    : "bg-[#2A2A26] text-[#5C5C58] cursor-not-allowed"
                }`}
                data-testid="lesson-next-button"
                title={canFinish ? "" : `Watch ${remainingPct}% more to unlock`}
              >
                <div className="text-right flex-1">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-white/70">
                    {canFinish ? "Next" : `${remainingPct}% to go`}
                  </div>
                  <div className="text-sm font-semibold truncate">{nextLesson.title}</div>
                </div>
                {canFinish ? <CaretRight size={18} /> : <Lock size={18} weight="bold" />}
              </button>
            ) : (
              <button
                onClick={() => canFinish && markCompleteAndGo(`/courses/${courseId}`)}
                disabled={!canFinish}
                className={`flex-1 rounded-lg px-5 py-3 inline-flex items-center justify-end gap-3 transition-colors ${
                  canFinish
                    ? "bg-[#4A6B53] hover:bg-[#3E5A45] text-white"
                    : "bg-[#2A2A26] text-[#5C5C58] cursor-not-allowed"
                }`}
                data-testid="lesson-finish-button"
                title={canFinish ? "" : `Watch ${remainingPct}% more to unlock`}
              >
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-white/70">
                    {canFinish ? "Done" : `${remainingPct}% to go`}
                  </div>
                  <div className="text-sm font-semibold">
                    {canFinish ? "Mark complete & back to course" : "Watch full video to complete"}
                  </div>
                </div>
                {canFinish ? <CheckCircle size={18} weight="fill" /> : <Lock size={18} weight="bold" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar (desktop) */}
      {!sidebarHidden && <div className="hidden lg:block">{sidebar}</div>}

      {/* Sidebar (mobile drawer) */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setDrawerOpen(false)}>
          <div
            className="absolute right-0 top-0 bottom-0 w-80 max-w-[90vw]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-full">
              <button
                onClick={() => setDrawerOpen(false)}
                className="absolute top-3 right-3 p-2 z-10 bg-white border border-[#EAEAE6]"
              >
                <X size={18} />
              </button>
              {sidebar}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
